import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { DeckShell } from './DeckShell';
import { Building2Icon, SparklesIcon, LayoutDashboardIcon, FileTextIcon, TrendingUpIcon, CheckCircle2Icon, ClockIcon, ArrowRightIcon } from 'lucide-react';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 24,
    filter: 'blur(8px)'
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)'
  },
  transition: {
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1]
  }
};
const scaleIn = {
  initial: {
    opacity: 0,
    scale: 0.92
  },
  animate: {
    opacity: 1,
    scale: 1
  },
  transition: {
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1]
  }
};
const slideRight = {
  initial: {
    opacity: 0,
    x: -30
  },
  animate: {
    opacity: 1,
    x: 0
  },
  transition: {
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }
};
const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
// Co-branded header for all slides
const CoBrandHeader = () => <div className="absolute top-8 left-8 md:left-12 z-20 flex items-center gap-4" data-id="element-2214">
    <div className="font-serif-display text-lg font-bold text-[#0B1B3B]" data-id="element-2215">
      Lamplighter Inn
    </div>
    <div className="w-px h-4 bg-slate-300" data-id="element-2216" />
    <div className="font-bold text-sm tracking-tight flex items-start leading-none" data-id="element-2217">
      <span className="text-[#0B1B3B]" data-id="element-2218">Offsite</span>
      <span className="text-brand-coral font-black relative -top-0.5 ml-0.5" data-id="element-2219">
        +
      </span>
    </div>
  </div>;
export function LamplighterDeckPage() {
  const slides = [
  // 1) TITLE
  <div key="1" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-5xl mx-auto w-full relative overflow-hidden" data-id="element-2220">
      <motion.div animate={{
      scale: [1, 1.1, 1],
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 9,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(182,130,75,0.25)_0%,transparent_70%)] pointer-events-none" data-id="element-2221" />
      <motion.div variants={stagger} initial="initial" animate="animate" className="z-10" data-id="element-2222">
        <motion.div variants={fadeUp} className="text-[#B6824B] text-sm font-bold uppercase tracking-[0.2em] mb-6" data-id="element-2223">
          Lamplighter Inn · Best Western
        </motion.div>
        <motion.h1 variants={{
        initial: {
          opacity: 0,
          y: 40,
          filter: 'blur(12px)'
        },
        animate: {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }
      }} transition={{
        duration: 1,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-serif-display text-6xl md:text-8xl font-bold text-[#0B1B3B] mb-6 leading-[1.1] tracking-tight" data-id="element-2224">
          Win the next era of <br data-id="element-2225" /> group business.
        </motion.h1>
        <motion.p variants={fadeUp} className="text-xl md:text-2xl text-slate-600 mb-16 max-w-2xl leading-relaxed" data-id="element-2226">
          A pitch from Offsite+ for Lamplighter Inn leadership.
        </motion.p>
        <motion.div variants={fadeUp} className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2227">
          Prepared for: General Manager · 2026
        </motion.div>
      </motion.div>
    </div>,
  // 2) THE THESIS
  <div key="2" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-5xl mx-auto w-full relative" data-id="element-2228">
      <CoBrandHeader data-id="element-2229" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2230">
        <motion.div variants={fadeUp} className="text-[#B6824B] text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2231">
          Why we're here
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-6xl font-bold text-[#0B1B3B] mb-16 leading-tight max-w-3xl" data-id="element-2232">
          Your meeting space and room block are undermonetized.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-2233">
          {['Group RFPs are won on price, driving down margins.', 'Repeat groups are rare because the experience is generic.', 'Margins compress every year while your competition catches up.'].map((text, i) => <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm" data-id="element-2234">
              <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center font-bold mb-6" data-id="element-2235">
                !
              </div>
              <p className="text-lg text-slate-700 leading-relaxed font-medium" data-id="element-2236">
                {text}
              </p>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 3) THE OPPORTUNITY
  <div key="3" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-6xl mx-auto w-full relative" data-id="element-2237">
      <CoBrandHeader data-id="element-2238" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2239">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-6xl font-bold text-[#0B1B3B] mb-16 leading-tight text-center" data-id="element-2240">
          Don't sell rooms.{' '}
          <span className="text-[#B6824B]" data-id="element-2241">Sell transformation.</span>
        </motion.h2>
        <div className="flex flex-col md:flex-row gap-8 items-center max-w-4xl mx-auto" data-id="element-2242">
          <motion.div variants={fadeUp} className="flex-1 w-full bg-slate-100 rounded-2xl p-10 text-center border border-slate-200" data-id="element-2243">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-6" data-id="element-2244">
              Group Block (Today)
            </div>
            <div className="font-serif-display text-6xl font-bold text-slate-400 mb-2" data-id="element-2245">
              $189
            </div>
            <div className="text-slate-500 font-medium" data-id="element-2246">per room / night</div>
          </motion.div>
          <motion.div variants={fadeUp} className="w-12 h-12 shrink-0 rounded-full bg-white shadow-md flex items-center justify-center z-10 -my-4 md:-mx-4 md:my-0" data-id="element-2247">
            <ArrowRightIcon className="w-6 h-6 text-slate-300" data-id="element-2248" />
          </motion.div>
          <motion.div variants={fadeUp} className="flex-1 w-full bg-[#0B1B3B] rounded-2xl p-10 text-center shadow-xl" data-id="element-2249">
            <div className="text-sm font-bold text-[#B6824B] uppercase tracking-wider mb-6" data-id="element-2250">
              Offsite+ Retreat Package
            </div>
            <div className="font-serif-display text-6xl font-bold text-white mb-2" data-id="element-2251">
              $350
            </div>
            <div className="text-slate-300 font-medium" data-id="element-2252">per attendee / day</div>
          </motion.div>
        </div>
      </motion.div>
    </div>,
  // 4) WHAT IS OFFSITE+
  <div key="4" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-6xl mx-auto w-full relative" data-id="element-2253">
      <CoBrandHeader data-id="element-2254" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2255">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-8 leading-tight" data-id="element-2256">
          What is Offsite+?
        </motion.h2>
        <motion.p variants={fadeUp} className="text-xl text-slate-600 mb-16 max-w-3xl leading-relaxed" data-id="element-2257">
          We provide the technology and content layer that turns your physical
          property into a premium destination for corporate offsites, leadership
          retreats, and team building.
        </motion.p>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-2258">
          {[{
          icon: SparklesIcon,
          title: 'AI Designer',
          desc: 'Generate a world-class agenda using your specific property amenities.'
        }, {
          icon: LayoutDashboardIcon,
          title: 'Branded Event Hub',
          desc: 'A white-labeled companion app for attendees during their stay.'
        }, {
          icon: FileTextIcon,
          title: 'Outcomes Report',
          desc: 'Automated ROI metrics to prove the value of the retreat to the client.'
        }].map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm" data-id="element-2259">
                <div className="w-12 h-12 rounded-xl bg-[#FAF6EF] text-[#B6824B] flex items-center justify-center mb-6" data-id="element-2260">
                  <Icon className="w-6 h-6" data-id="element-2261" />
                </div>
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-3" data-id="element-2262">
                  {item.title}
                </h3>
                <p className="text-slate-600" data-id="element-2263">{item.desc}</p>
              </motion.div>;
        })}
        </div>
      </motion.div>
    </div>,
  // 5) THE LAMPLIGHTER EXPERIENCE
  <div key="5" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-6xl mx-auto w-full relative" data-id="element-2264">
      <CoBrandHeader data-id="element-2265" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2266">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-12 leading-tight" data-id="element-2267">
          A leadership retreat designed for Lamplighter Inn.
        </motion.h2>
        <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-12" data-id="element-2268">
          {['75 rooms', '2 ballrooms', 'Fire-pit lounge', 'Waterslide patio', 'Fitness studio'].map((spec, i) => <div key={i} className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-bold text-slate-600 shadow-sm" data-id="element-2269">
              {spec}
            </div>)}
        </motion.div>
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm max-w-3xl" data-id="element-2270">
          <p className="text-lg text-slate-600 leading-relaxed italic" data-id="element-2271">
            "We feed your property's exact specs into our AI Designer. When a
            client asks for a 3-day leadership retreat, we instantly generate an
            agenda that utilizes your waterslide patio for welcome drinks, your
            ballroom for morning strategy, and your fire-pit for evening
            reflection."
          </p>
        </motion.div>
      </motion.div>
    </div>,
  // 6) SAMPLE 3-DAY AGENDA — phone mockup
  <div key="6" className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20 pt-24 pb-20 px-8 md:px-20 max-w-6xl mx-auto w-full relative" data-id="element-2272">
      <CoBrandHeader data-id="element-2273" />
      {/* Left: copy */}
      <motion.div variants={stagger} initial="initial" animate="animate" className="flex-1 max-w-md" data-id="element-2274">
        <motion.div variants={fadeUp} className="text-[#B6824B] text-xs font-bold uppercase tracking-[0.2em] mb-3" data-id="element-2275">
          Sample Itinerary
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B1B3B] mb-5 leading-tight" data-id="element-2276">
          Every guest gets a private retreat companion.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-base text-slate-600 leading-relaxed mb-6" data-id="element-2277">
          The moment a corporate group is booked, every attendee receives a
          co-branded mobile experience with their full agenda, pre-work, and
          live updates — all powered by Offsite+, wrapped in your hospitality.
        </motion.p>
        <motion.div variants={fadeUp} className="flex items-center gap-2 text-sm text-[#B6824B] font-bold" data-id="element-2278">
          <CheckCircle2Icon className="w-4 h-4" data-id="element-2279" />
          Co-branded · Auto-generated · Mobile-first
        </motion.div>
      </motion.div>

      {/* Right: phone with auto-scrolling agenda */}
      <motion.div variants={scaleIn} initial="initial" animate="animate" className="flex-shrink-0" data-id="element-2280">
        <div className="relative w-[280px] h-[560px] bg-[#0B1B3B] rounded-[44px] p-3 shadow-2xl" data-id="element-2281">
          {/* Notch */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#0B1B3B] rounded-b-2xl z-20" data-id="element-2282" />
          {/* Screen */}
          <div className="w-full h-full bg-[#FAF6EF] rounded-[34px] overflow-hidden relative" data-id="element-2283">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 bg-white border-b border-slate-100 px-5 pt-8 pb-3 z-10" data-id="element-2284">
              <div className="flex items-center gap-2 mb-2" data-id="element-2285">
                <div className="font-serif-display text-xs font-bold text-[#0B1B3B]" data-id="element-2286">
                  Lamplighter Inn
                </div>
                <div className="w-px h-3 bg-slate-300" data-id="element-2287" />
                <div className="font-bold text-[10px] flex items-start leading-none" data-id="element-2288">
                  <span className="text-[#0B1B3B]" data-id="element-2289">Offsite</span>
                  <span className="text-brand-coral font-black relative -top-0.5 ml-0.5" data-id="element-2290">
                    +
                  </span>
                </div>
              </div>
              <div className="text-[10px] font-bold text-[#B6824B] uppercase tracking-wider" data-id="element-2291">
                Executive Alignment Retreat
              </div>
            </div>

            {/* Scrolling agenda */}
            <motion.div className="pt-24 px-4 pb-10 space-y-5" animate={{
            y: [0, -180, -180, 0, 0]
          }} transition={{
            duration: 10,
            times: [0, 0.4, 0.5, 0.9, 1],
            ease: 'easeInOut',
            repeat: Infinity,
            repeatDelay: 0.5
          }} data-id="element-2292">
              {[{
              day: 'Day 1',
              items: ['15:00 — Arrival & Check-in', '18:00 — Welcome Dinner · Waterslide Patio']
            }, {
              day: 'Day 2',
              items: ['09:00 — Leadership Module · Ballroom A', '12:30 — Working Lunch', '14:00 — Outdoor Team Activity', '20:00 — Fire-pit Reflection']
            }, {
              day: 'Day 3',
              items: ['09:00 — Strategic Planning Workshop', '12:00 — Closing Brunch & Departures']
            }].map((day, i) => <div key={i} data-id="element-2293">
                  <div className="font-serif-display text-base font-bold text-[#B6824B] mb-2" data-id="element-2294">
                    {day.day}
                  </div>
                  <div className="space-y-1.5" data-id="element-2295">
                    {day.items.map((item, j) => <div key={j} className="flex items-start gap-2 bg-white px-2.5 py-2 rounded-lg border border-slate-100 shadow-sm" data-id="element-2296">
                        <ClockIcon className="w-3 h-3 text-slate-400 shrink-0 mt-0.5" data-id="element-2297" />
                        <span className="text-[11px] font-medium text-slate-700 leading-snug" data-id="element-2298">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>)}
            </motion.div>

            {/* Top fade */}
            <div className="absolute top-[88px] left-0 right-0 h-6 bg-gradient-to-b from-[#FAF6EF] to-transparent pointer-events-none z-10" data-id="element-2299" />
            {/* Bottom fade */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#FAF6EF] to-transparent pointer-events-none" data-id="element-2300" />
          </div>
        </div>
      </motion.div>
    </div>,
  // 7) PROJECTED REVENUE UPLIFT
  <div key="7" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-5xl mx-auto w-full relative" data-id="element-2301">
      <CoBrandHeader data-id="element-2302" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2303">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-16 leading-tight" data-id="element-2304">
          The financial impact.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-2305">
          {[{
          label: 'Avg upsell per retreat',
          val: '$32,500'
        }, {
          label: 'Target retreats / year',
          val: '12'
        }, {
          label: 'Incremental annual revenue',
          val: '$390,000',
          highlight: true
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className={`rounded-2xl p-8 ${item.highlight ? 'bg-[#0B1B3B] text-white shadow-xl' : 'bg-white border border-slate-200 text-[#0B1B3B]'}`} data-id="element-2306">
              <div className={`text-sm font-bold uppercase tracking-wider mb-4 ${item.highlight ? 'text-[#B6824B]' : 'text-slate-500'}`} data-id="element-2307">
                {item.label}
              </div>
              <div className="font-serif-display text-5xl font-bold" data-id="element-2308">
                {item.val}
              </div>
            </motion.div>)}
        </div>
        <motion.p variants={fadeUp} className="text-sm text-slate-500 italic" data-id="element-2309">
          * Excludes F&B uplift and increased ADR from premium positioning.
        </motion.p>
      </motion.div>
    </div>,
  // 8) PROPOSAL BUILDER PREVIEW — full laptop mockup
  <div key="8" className="flex-1 flex flex-col justify-center items-center pt-20 pb-16 px-8 md:px-12 max-w-6xl mx-auto w-full relative" data-id="element-2310">
      <CoBrandHeader data-id="element-2311" />
      <motion.div variants={stagger} initial="initial" animate="animate" className="w-full" data-id="element-2312">
        <motion.h2 variants={fadeUp} className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B1B3B] mb-3 text-center" data-id="element-2313">
          Win the RFP with a premium proposal.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-sm text-slate-500 text-center mb-8" data-id="element-2314">
          What your sales team ships to a buyer — generated in under 90 seconds.
        </motion.p>

        {/* Laptop frame */}
        <motion.div variants={scaleIn} className="w-full max-w-5xl mx-auto" data-id="element-2315">
          <div className="bg-slate-800 rounded-t-2xl p-2 shadow-2xl" data-id="element-2316">
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 px-2 pb-1.5" data-id="element-2317">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-2318" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-2319" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-2320" />
              <div className="ml-3 px-2 py-0.5 bg-slate-700 rounded text-[9px] text-slate-300 font-mono" data-id="element-2321">
                lamplighterinn.com/proposal/acme-corp
              </div>
            </div>
            {/* Proposal content */}
            <div className="bg-white rounded-lg overflow-hidden" data-id="element-2322">
              {/* Hero band */}
              <div className="bg-[#0B1B3B] text-white p-6 relative overflow-hidden" data-id="element-2323">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1200&q=80')] opacity-20 bg-cover bg-center" data-id="element-2324" />
                <div className="relative z-10 flex items-start justify-between gap-6" data-id="element-2325">
                  <div data-id="element-2326">
                    <div className="text-[9px] font-bold text-[#B6824B] uppercase tracking-[0.2em] mb-2" data-id="element-2327">
                      Premium Upgrade Proposal
                    </div>
                    <h3 className="font-serif-display text-2xl font-bold leading-tight mb-1" data-id="element-2328">
                      Executive Alignment Retreat
                    </h3>
                    <p className="text-[11px] text-slate-300" data-id="element-2329">
                      Prepared exclusively for Cedarwood Group · 24 attendees ·
                      Apr 14–16, 2026
                    </p>
                  </div>
                  <div className="text-right shrink-0" data-id="element-2330">
                    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-2331">
                      Total Investment
                    </div>
                    <div className="font-serif-display text-2xl font-bold" data-id="element-2332">
                      $84,200
                    </div>
                    <div className="text-[9px] text-[#B6824B]" data-id="element-2333">
                      +$32,500 vs. base
                    </div>
                  </div>
                </div>
              </div>

              {/* Body grid */}
              <div className="grid grid-cols-12 gap-3 p-4 text-[10px]" data-id="element-2334">
                {/* Agenda */}
                <div className="col-span-7 space-y-2" data-id="element-2335">
                  <div className="text-[9px] font-bold text-[#B6824B] uppercase tracking-wider" data-id="element-2336">
                    3-Day Agenda
                  </div>
                  {[{
                  day: 'Day 1',
                  items: ['15:00 — Arrival & Check-in', '18:00 — Welcome Dinner · Waterslide Patio']
                }, {
                  day: 'Day 2',
                  items: ['09:00 — Leadership Module · Ballroom A', '14:00 — Outdoor Team Activity', '20:00 — Fire-pit Reflection']
                }, {
                  day: 'Day 3',
                  items: ['09:00 — Strategic Planning Workshop', '12:00 — Closing Brunch']
                }].map((d, i) => <div key={i} className="flex gap-2" data-id="element-2337">
                      <div className="w-9 shrink-0 font-serif-display text-xs font-bold text-[#B6824B]" data-id="element-2338">
                        {d.day}
                      </div>
                      <div className="flex-1 space-y-1" data-id="element-2339">
                        {d.items.map((it, j) => <div key={j} className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100" data-id="element-2340">
                            <ClockIcon className="w-2.5 h-2.5 text-slate-400 shrink-0" data-id="element-2341" />
                            <span className="text-[9px] font-medium text-slate-700" data-id="element-2342">
                              {it}
                            </span>
                          </div>)}
                      </div>
                    </div>)}
                </div>

                {/* Right column: modules + line items */}
                <div className="col-span-5 space-y-3" data-id="element-2343">
                  <div data-id="element-2344">
                    <div className="text-[9px] font-bold text-[#B6824B] uppercase tracking-wider mb-1.5" data-id="element-2345">
                      Licensed Modules
                    </div>
                    <div className="space-y-1" data-id="element-2346">
                      {['Leadership · Harvard', 'Strategy · INSEAD', 'Team Dynamics · Ivey'].map((m, i) => <div key={i} className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100" data-id="element-2347">
                          <CheckCircle2Icon className="w-2.5 h-2.5 text-[#B6824B] shrink-0" data-id="element-2348" />
                          <span className="text-[9px] font-medium text-slate-700" data-id="element-2349">
                            {m}
                          </span>
                        </div>)}
                    </div>
                  </div>
                  <div data-id="element-2350">
                    <div className="text-[9px] font-bold text-[#B6824B] uppercase tracking-wider mb-1.5" data-id="element-2351">
                      Investment Summary
                    </div>
                    <div className="space-y-1 text-[9px]" data-id="element-2352">
                      {[['Lodging · 24 rooms × 2 nights', '$28,800'], ['F&B · all-inclusive', '$22,900'], ['Facilitation & content', '$24,500'], ['Premium experiences', '$8,000']].map(([label, val], i) => <div key={i} className="flex items-center justify-between border-b border-slate-100 pb-1" data-id="element-2353">
                          <span className="text-slate-600" data-id="element-2354">{label}</span>
                          <span className="font-semibold text-[#0B1B3B]" data-id="element-2355">
                            {val}
                          </span>
                        </div>)}
                      <div className="flex items-center justify-between pt-1" data-id="element-2356">
                        <span className="font-bold text-[#0B1B3B]" data-id="element-2357">Total</span>
                        <span className="font-bold text-[#0B1B3B]" data-id="element-2358">
                          $84,200
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer CTA */}
              <div className="bg-slate-50 border-t border-slate-100 px-4 py-2 flex items-center justify-between" data-id="element-2359">
                <div className="text-[9px] text-slate-500" data-id="element-2360">
                  Hold valid until Mar 31 · Auto-generated by Offsite+
                </div>
                <div className="flex items-center gap-1.5 bg-[#B6824B] text-white px-2.5 py-1 rounded text-[9px] font-bold" data-id="element-2361">
                  Accept & Sign <ArrowRightIcon className="w-2.5 h-2.5" data-id="element-2362" />
                </div>
              </div>
            </div>
          </div>
          {/* Laptop base */}
          <div className="h-2 bg-slate-700 rounded-b-xl mx-auto w-[105%] -ml-[2.5%] shadow-lg" data-id="element-2363" />
          <div className="h-1.5 bg-slate-800 rounded-b-2xl mx-auto w-[40%] shadow-lg" data-id="element-2364" />
        </motion.div>
      </motion.div>
    </div>,
  // 9) WORLD-CLASS CONTENT
  <div key="9" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-5xl mx-auto w-full relative" data-id="element-2365">
      <CoBrandHeader data-id="element-2366" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2367">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-8 leading-tight" data-id="element-2368">
          Your retreats taught by the world's best.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-xl text-slate-600 mb-16" data-id="element-2369">
          We license modules; you deliver them under your brand.
        </motion.p>
        <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-3 gap-8 opacity-60 grayscale" data-id="element-2370">
          {['Harvard Business School', 'Wharton', 'Ivey Business School', 'INSEAD', 'Stanford GSB', 'MIT Sloan', 'Kellogg', 'Columbia Business School'].map((school, i) => <div key={i} className="font-serif-display text-2xl font-bold text-center text-slate-800" data-id="element-2371">
              {school}
            </div>)}
        </motion.div>
      </motion.div>
    </div>,
  // 10) THE PARTNER REVENUE PORTAL — laptop mockup
  <div key="10" className="flex-1 flex flex-col justify-center pt-20 pb-16 px-8 md:px-12 max-w-6xl mx-auto w-full relative" data-id="element-2372">
      <CoBrandHeader data-id="element-2373" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2374">
        <motion.h2 variants={fadeUp} className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B1B3B] mb-3 leading-tight text-center" data-id="element-2375">
          The Partner Revenue Portal
        </motion.h2>
        <motion.p variants={fadeUp} className="text-sm text-slate-500 mb-8 text-center" data-id="element-2376">
          A live view of upsell revenue, pipeline, and conversion — for every
          property leader.
        </motion.p>

        {/* Laptop frame */}
        <motion.div variants={scaleIn} className="w-full max-w-5xl mx-auto" data-id="element-2377">
          <div className="bg-slate-800 rounded-t-2xl p-2 shadow-2xl" data-id="element-2378">
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 px-2 pb-1.5" data-id="element-2379">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-2380" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-2381" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-2382" />
              <div className="ml-3 px-2 py-0.5 bg-slate-700 rounded text-[9px] text-slate-300 font-mono" data-id="element-2383">
                partners.offsiteplus.com/lamplighter
              </div>
            </div>
            {/* Dashboard content */}
            <div className="bg-white rounded-lg overflow-hidden p-5" data-id="element-2384">
              {/* Header row */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100" data-id="element-2385">
                <div data-id="element-2386">
                  <div className="text-[9px] font-bold text-[#B6824B] uppercase tracking-wider mb-0.5" data-id="element-2387">
                    Lamplighter Inn · 2026 YTD
                  </div>
                  <div className="font-serif-display text-lg font-bold text-[#0B1B3B]" data-id="element-2388">
                    Revenue Performance
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[9px]" data-id="element-2389">
                  <div className="px-2 py-1 bg-slate-100 rounded text-slate-600 font-semibold" data-id="element-2390">
                    YTD
                  </div>
                  <div className="px-2 py-1 bg-white border border-slate-200 rounded text-slate-400" data-id="element-2391">
                    QTD
                  </div>
                  <div className="px-2 py-1 bg-white border border-slate-200 rounded text-slate-400" data-id="element-2392">
                    MTD
                  </div>
                </div>
              </div>

              {/* KPI grid */}
              <div className="grid grid-cols-4 gap-3 mb-4" data-id="element-2393">
                {[{
                label: 'Upsell Revenue YTD',
                val: '$850,000',
                delta: '+38%',
                deltaUp: true
              }, {
                label: 'Identified Pipeline',
                val: '$210,000',
                delta: '14 open',
                deltaUp: true
              }, {
                label: 'Avg Upgrade Value',
                val: '$32,500',
                delta: '+$4.2K',
                deltaUp: true
              }, {
                label: 'Conversion',
                val: '22%',
                delta: '+6 pts',
                deltaUp: true
              }].map((item, i) => <div key={i} className="bg-slate-50 rounded-lg border border-slate-100 p-3" data-id="element-2394">
                    <div className="text-[8px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-2395">
                      {item.label}
                    </div>
                    <div className="font-serif-display text-xl font-bold text-[#0B1B3B] mb-1" data-id="element-2396">
                      {item.val}
                    </div>
                    <div className="flex items-center gap-1 text-[8px] font-bold text-emerald-600" data-id="element-2397">
                      <TrendingUpIcon className="w-2.5 h-2.5" data-id="element-2398" />
                      {item.delta}
                    </div>
                  </div>)}
              </div>

              {/* Chart + side panel */}
              <div className="grid grid-cols-12 gap-3" data-id="element-2399">
                {/* Chart */}
                <div className="col-span-7 bg-slate-50 rounded-lg border border-slate-100 p-3" data-id="element-2400">
                  <div className="flex items-center justify-between mb-2" data-id="element-2401">
                    <div className="text-[9px] font-bold text-[#0B1B3B]" data-id="element-2402">
                      Monthly Upsell Revenue
                    </div>
                    <div className="text-[8px] text-slate-400" data-id="element-2403">
                      Jan — Dec 2026
                    </div>
                  </div>
                  <div className="flex items-end gap-1 h-16" data-id="element-2404">
                    {[28, 42, 35, 58, 64, 72, 81, 70, 88, 95, 102, 115].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${h}%`
                  }} transition={{
                    duration: 0.6,
                    delay: 0.3 + i * 0.06,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="flex-1 bg-gradient-to-t from-[#B6824B] to-[#D4A574] rounded-sm" data-id="element-2405" />)}
                  </div>
                </div>

                {/* High-probability upsells */}
                <div className="col-span-5 bg-slate-50 rounded-lg border border-slate-100 p-3" data-id="element-2406">
                  <div className="text-[9px] font-bold text-[#0B1B3B] mb-2" data-id="element-2407">
                    High-Probability Upsells
                  </div>
                  <div className="space-y-1.5" data-id="element-2408">
                    {[{
                    name: 'Acme Corp · 24 pax',
                    val: '$32K',
                    tag: '92%'
                  }, {
                    name: 'Sierra Health · 18 pax',
                    val: '$28K',
                    tag: '87%'
                  }, {
                    name: 'Northwind · 32 pax',
                    val: '$41K',
                    tag: '78%'
                  }].map((u, i) => <div key={i} className="flex items-center justify-between bg-white px-2 py-1.5 rounded border border-slate-100" data-id="element-2409">
                        <div data-id="element-2410">
                          <div className="text-[9px] font-semibold text-[#0B1B3B] leading-tight" data-id="element-2411">
                            {u.name}
                          </div>
                          <div className="text-[8px] text-slate-400" data-id="element-2412">
                            {u.val} potential
                          </div>
                        </div>
                        <div className="text-[8px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-2413">
                          {u.tag}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Laptop base */}
          <div className="h-2 bg-slate-700 rounded-b-xl mx-auto w-[105%] -ml-[2.5%] shadow-lg" data-id="element-2414" />
          <div className="h-1.5 bg-slate-800 rounded-b-2xl mx-auto w-[40%] shadow-lg" data-id="element-2415" />
        </motion.div>
      </motion.div>
    </div>,
  // 11) WHY BEST WESTERN PROPERTIES WIN
  <div key="11" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-5xl mx-auto w-full relative" data-id="element-2416">
      <CoBrandHeader data-id="element-2417" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2418">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-16 leading-tight" data-id="element-2419">
          Why Best Western properties win with Offsite+
        </motion.h2>
        <div className="space-y-6" data-id="element-2420">
          {[{
          title: 'Local Owner-Operators',
          desc: 'Decision speed. You can implement and sell this faster than corporate-owned mega-resorts.'
        }, {
          title: 'Under-utilized Inventory',
          desc: 'Fill off-peak days and shoulder seasons with high-margin corporate retreats.'
        }, {
          title: 'Community Connection',
          desc: 'A compelling story for local and regional corporate buyers looking for authentic experiences.'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-6 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm" data-id="element-2421">
              <CheckCircle2Icon className="w-8 h-8 text-[#B6824B] shrink-0" data-id="element-2422" />
              <div data-id="element-2423">
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2" data-id="element-2424">
                  {item.title}
                </h3>
                <p className="text-slate-600" data-id="element-2425">{item.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 12) ONBOARDING
  <div key="12" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-6xl mx-auto w-full relative" data-id="element-2426">
      <CoBrandHeader data-id="element-2427" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2428">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-20 leading-tight" data-id="element-2429">
          30 days to first booked retreat.
        </motion.h2>
        <div className="relative" data-id="element-2430">
          <div className="absolute top-6 left-0 w-full h-px bg-slate-300" data-id="element-2431" />
          <div className="grid grid-cols-3 gap-8" data-id="element-2432">
            {[{
            days: 'Week 1',
            title: 'Onboarding & Data Ingestion',
            desc: 'Property specs loaded, branded portal live, and we ingest your existing booking list for the year to power the prediction engine.'
          }, {
            days: 'Week 2',
            title: 'First Proposal',
            desc: 'Sales team trained, first upgrade proposal generated for an existing group.'
          }, {
            days: 'Weeks 3-4',
            title: 'First Booking',
            desc: 'First retreat booked and agenda finalized — ready to deliver.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative pt-12" data-id="element-2433">
                <div className="absolute top-4 left-0 w-4 h-4 rounded-full bg-[#B6824B] border-4 border-[#FAF6EF]" data-id="element-2434" />
                <div className="text-sm font-bold text-[#B6824B] uppercase tracking-wider mb-2" data-id="element-2435">
                  {item.days}
                </div>
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2" data-id="element-2436">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600" data-id="element-2437">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </motion.div>
    </div>,
  // 13) THE ASK
  <div key="13" className="flex-1 flex flex-col justify-center pt-24 pb-20 px-8 md:px-20 max-w-5xl mx-auto w-full relative" data-id="element-2438">
      <CoBrandHeader data-id="element-2439" />
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2440">
        <motion.div variants={fadeUp} className="text-[#B6824B] text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2441">
          The Ask
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-5xl md:text-7xl font-bold text-[#0B1B3B] mb-16 leading-tight" data-id="element-2442">
          Run 2 retreat pilots <br data-id="element-2443" /> in 90 days.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-2444">
          {['AI Designer + branded portal', '50/50 profit split on retreats', '$0 platform fee for pilot'].map((text, i) => <motion.div key={i} variants={fadeUp} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm text-center font-bold text-slate-700" data-id="element-2445">
              {text}
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 14) CLOSING
  <div key="14" className="flex-1 flex flex-col items-center justify-center relative px-6 text-center" data-id="element-2446">
      <motion.div variants={stagger} initial="initial" animate="animate" className="z-10" data-id="element-2447">
        <motion.h2 variants={fadeUp} className="font-serif-display text-5xl md:text-7xl font-bold text-[#0B1B3B] mb-8" data-id="element-2448">
          Lamplighter Inn × Offsite+
        </motion.h2>
        <motion.p variants={fadeUp} className="text-2xl text-slate-600 mb-16 font-light" data-id="element-2449">
          The new era of group hospitality starts here.
        </motion.p>
        <motion.div variants={fadeUp} data-id="element-2450">
          <a href="mailto:partners@offsiteplus.com" className="text-[#B6824B] hover:text-[#0B1B3B] transition-colors font-bold tracking-wider uppercase text-sm" data-id="element-2451">
            partners@offsiteplus.com
          </a>
        </motion.div>
      </motion.div>
    </div>];
  return <DeckShell slides={slides} theme="light" brandLabel="Lamplighter Inn Pitch" data-id="element-2452" />;
}